import { IChecklistItemRow } from "@mctypes/checklist.types";
import { openapi } from "../../../../openapi";
import { setShowFailedMessageThunk, setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { selectCurrentProjectId } from "../../../global/globalSliceCommon";
import { AppThunk } from "../../../store";
import { selectActiveChecklist } from "./checklistSelectors";
import { setRefactoredCollapsedCategoryIdsInRedux, setRefactoredExpandedCategoryIdsInRedux } from "./checklistSlice";

type RefactoredCategoryCollapseState = {
  allCategoriesCollapsed: boolean;
  categoryIds: string[];
  collapsedCategoryIds: Set<string>;
};

const setRefactoredCategoryCollapseState =
  (
    categoryIds: string[],
    collapsed: boolean,
    refactoredCollapsedCategoryIds: string[],
    refactoredExpandedCategoryIds: string[],
  ): AppThunk =>
  (dispatch) => {
    const categoryIdSet = new Set(categoryIds);

    if (collapsed) {
      dispatch(
        setRefactoredCollapsedCategoryIdsInRedux([
          ...new Set([...refactoredCollapsedCategoryIds, ...categoryIds]),
        ]),
      );
      dispatch(
        setRefactoredExpandedCategoryIdsInRedux(refactoredExpandedCategoryIds.filter((id) => !categoryIdSet.has(id))),
      );
      return;
    }

    dispatch(setRefactoredExpandedCategoryIdsInRedux([...new Set([...refactoredExpandedCategoryIds, ...categoryIds])]));
    dispatch(
      setRefactoredCollapsedCategoryIdsInRedux(
        refactoredCollapsedCategoryIds.filter((id) => !categoryIdSet.has(id)),
      ),
    );
  };

export const thunkToggleRefactoredCategoryCollapse =
  (
    categoryId: string,
    collapseState: RefactoredCategoryCollapseState,
    refactoredCollapsedCategoryIds: string[],
    refactoredExpandedCategoryIds: string[],
  ): AppThunk =>
  (dispatch) => {
    dispatch(
      setRefactoredCategoryCollapseState(
        [categoryId],
        collapseState.collapsedCategoryIds.has(categoryId) === false,
        refactoredCollapsedCategoryIds,
        refactoredExpandedCategoryIds,
      ),
    );
  };

export const thunkToggleAllRefactoredCategoriesCollapse =
  (
    collapseState: RefactoredCategoryCollapseState,
    refactoredCollapsedCategoryIds: string[],
    refactoredExpandedCategoryIds: string[],
  ): AppThunk =>
  (dispatch) => {
    dispatch(
      setRefactoredCategoryCollapseState(
        collapseState.categoryIds,
        collapseState.allCategoriesCollapsed === false,
        refactoredCollapsedCategoryIds,
        refactoredExpandedCategoryIds,
      ),
    );
  };

export const thunkUpdateChecklistItem =
  (checklistId: string, itemId: string, path: string, oldValue: unknown, newValue: unknown): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const projectId = selectCurrentProjectId(state);
    const checklist = selectActiveChecklist(state, checklistId);

    if (projectId == null || checklist?._id == null) return;

    try {
      await dispatch(
        openapi.endpoints.putProjectByProjectIdChecklistAndChecklistIdItemItemId.initiate({
          projectId,
          checklistId: checklist._id,
          itemId,
          body: {
            path,
            oldValue,
            newValue,
          },
        }),
      ).unwrap();
      dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
    } catch {
      dispatch(setShowFailedMessageThunk("Feil ved oppdatering av sjekklisterad"));
    }
  };

export const thunkUpdateChecklistCategory =
  (
    checklistId: string,
    categoryId: string,
    attribute: string,
    oldValue: unknown,
    newValue: unknown,
  ): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const projectId = selectCurrentProjectId(state);
    const checklist = selectActiveChecklist(state, checklistId);

    if (projectId == null || checklist?._id == null) return;

    try {
      await dispatch(
        openapi.endpoints.putProjectByProjectIdChecklistAndChecklistIdCategoryCategoryId.initiate({
          projectId,
          checklistId: checklist._id,
          categoryId,
          body: {
            attribute,
            oldValue,
            newValue,
          },
        }),
      ).unwrap();
      dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
    } catch {
      dispatch(setShowFailedMessageThunk("Feil ved oppdatering av sjekklistekategori"));
    }
  };

export const thunkToggleChecklistInactive =
  (checklistId: string, row: IChecklistItemRow): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const nextValue = row.inactive?.isChecked !== true;

    if (row.originalItem != null) {
      await dispatch(
        thunkUpdateChecklistItem(
          checklistId,
          row._id,
          "inactive.isChecked",
          row.inactive?.isChecked?.toString() ?? "",
          nextValue,
        ),
      );
      return;
    }

    const state = getState();
    const projectId = selectCurrentProjectId(state);
    const checklist = selectActiveChecklist(state, checklistId);

    if (projectId == null || checklist?._id == null) return;

    try {
      await dispatch(
        openapi.endpoints.putProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdInactive.initiate({
          projectId,
          checklistId: checklist._id,
          categoryId: row._id,
          body: {
            inactive: nextValue,
          },
        }),
      ).unwrap();
      dispatch(setShowSuccessMessageThunk("Sjekkliste oppdatert"));
    } catch {
      dispatch(setShowFailedMessageThunk("Feil ved oppdatering av kategori"));
    }
  };
